import { useContainer } from "features/container/Feature";
import { useEditor } from "features/editor/Feature";
import { PreviewProvider } from "features/preview/Feature";
import type { TaskPortTab } from "features/types/tabs";
import { usePitcher } from "features/utils/usePitcher";
import { useCapabilities } from "hooks/pitcher/useCapabilities";
import { usePermissions } from "hooks/pitcher/usePermissions";
import { useSetupProgress } from "hooks/pitcher/useSetupProgress";
import { useTask } from "hooks/pitcher/useTask";
import { Dropdown, Text } from "prism-react";
import { useState } from "react";
import * as semver from "semver";

import { useAnalytics } from "hooks/useAnalytics";
import { canSeamlessBranch } from "utils/canSeamlessBranch";
import { withPitcherUrlProtocol } from "utils/url";
import { executeVSCodeCommand } from "utils/vscode";

import { Preview } from "./Preview/Preview";
import { DevtoolLoading } from "./common/DevtoolLoading";
import { DevtoolMessage } from "./common/DevtoolMessage";
import { DevtoolWrapper } from "./common/DevtoolWrapper";
import { ProcessKilledMessage } from "./common/ProcessKilledMessage";
import type { TabPanelProps } from "./types";

function getInitialPath(initialPath: string | undefined): string | undefined {
  if (initialPath) {
    return initialPath;
  }

  try {
    const urlQueryParams = new URLSearchParams(window.location.search);
    const previewPath = urlQueryParams.get("previewPath");
    if (previewPath) {
      return previewPath;
    }
  } catch (e) {
    /* Ignore */
  }

  return initialPath;
}

/**
 * The minimum version of Pitcher that supports saving task previews.
 * This is used to determine whether to show the "Set x as primary port" button.
 *
 * @TODO(@CompuIves): Remove this const in ~August.
 */
const MIN_SAVE_TASK_PREVIEW_VERSION = "0.285.0";

export const TaskPortDevtool: React.FC<TabPanelProps<TaskPortTab>> = ({
  panelId,
  tab,
}) => {
  const { taskId, id: tabId, port, path: initialPath } = tab;
  const [dismissStripePort, setDismissStripePort] = useState(false);

  const pitcher = usePitcher();
  const [container] = useContainer();
  const setupProgress = useSetupProgress();
  const project = useEditor();
  const pitcherCapabilities = useCapabilities();
  const pitcherPermissions = usePermissions();
  const [generatingPort, setGeneratingPort] = useState(false);
  const track = useAnalytics();

  const { task, killedBy, taskInitializing } = useTask(taskId);

  if (taskInitializing) {
    return (
      <DevtoolWrapper variant="preview">
        <DevtoolLoading />
      </DevtoolWrapper>
    );
  }

  if (!task) {
    return (
      <DevtoolWrapper variant="preview">
        <ProcessKilledMessage
          onAction={() => {
            executeVSCodeCommand("codesandbox.layout.closeDevtool", {
              type: "TASK_PORT",
              port,
              taskId,
            });
          }}
          processType="TASK"
        />
      </DevtoolWrapper>
    );
  }

  const canManageTask =
    task.shell &&
    container.state !== "RESTARTING" &&
    setupProgress.state !== "IN_PROGRESS"
      ? !!(pitcherCapabilities.task?.create && pitcherPermissions.task?.create)
      : false;

  const setPrimaryPort = async () => {
    setGeneratingPort(true);
    try {
      await pitcher.clients.task.updateTask(task.id, {
        preview: {
          ...task.preview,
          port,
        },
      });

      // Let Pitcher think
      // TODO(@CompuIves, @christianalfoni): This is a hack to make sure the
      // task is saved before we open the file. We should probably have a
      // better way of doing this.
      setTimeout(() => {
        // Open the file, to show what actually has changed.
        executeVSCodeCommand("codesandbox.openCsbTab", {
          type: "FILE",
          filepath: pitcher.clients.fs.resolveRelativeWorkspacePath(
            "/.codesandbox/tasks.json",
          ),
        });
      }, 100);
    } finally {
      setGeneratingPort(false);
    }
  };

  const { shell, ports, unconfigured } = task;

  const canSaveTaskToConfigFile =
    unconfigured &&
    ((pitcherCapabilities.fs?.operations && pitcherPermissions.fs?.operation) ||
      canSeamlessBranch(project));
  const canOpenLogs = shell && shell.status === "RUNNING";

  const portDetails = ports.find((p) => p.port === port);
  const isPortActive =
    Boolean(portDetails) &&
    Boolean(shell && shell.status === "RUNNING") &&
    container.state !== "RESTARTING" &&
    setupProgress.state !== "IN_PROGRESS";
  const hasSetPrimaryPortOfTask = Boolean(task && task.preview?.port && port);

  // This is passed down to the Preview, which renders the DevtoolWrapper component
  const secondaryActions = [];

  if (!hasSetPrimaryPortOfTask) {
    secondaryActions.push(
      <Dropdown.Item key="set-as-primary-preview" onSelect={setPrimaryPort}>
        Set as primary preview
      </Dropdown.Item>,
    );
  }

  if (canOpenLogs) {
    secondaryActions.push(
      <Dropdown.Item
        key={`open-log-${task.id}`}
        onSelect={async () => {
          executeVSCodeCommand("codesandbox.terminal.open", {
            taskId,
          });

          track("open_preview_logs", { event_source: "editor" });
        }}
      >
        Open logs
      </Dropdown.Item>,
    );
  }

  if (canSaveTaskToConfigFile) {
    secondaryActions.push(
      <Dropdown.Item
        key="save-to-config"
        onSelect={() => pitcher.clients.task.saveToConfig(task.id)}
      >
        Save to config
      </Dropdown.Item>,
    );
  }

  let canSavePreview;
  try {
    canSavePreview = semver.gte(
      pitcher.pitcherVersion,
      MIN_SAVE_TASK_PREVIEW_VERSION,
    );
  } catch (e) {
    canSavePreview = false;
  }
  const stripeActive =
    !dismissStripePort &&
    !hasSetPrimaryPortOfTask &&
    isPortActive &&
    pitcherPermissions.task?.generateConfig &&
    canSavePreview;

  const usedInitialPath = getInitialPath(initialPath);

  return (
    <PreviewProvider
      key={tabId}
      devtoolId={tabId}
      initialPath={usedInitialPath}
      origin={portDetails ? withPitcherUrlProtocol(portDetails.url) : ""}
    >
      <Preview
        canRestartProcess={canManageTask}
        disablePointerEvents={false}
        heightOffset={stripeActive ? 42 : 0}
        isPortActive={isPortActive}
        isPrimaryPort={task.preview?.port === port}
        killedBy={killedBy}
        onKillAction={() => {
          if (canManageTask) {
            track("restart_preview", { event_source: "editor" });
            pitcher.clients.task.runTask(taskId);
          }
        }}
        panelId={panelId}
        secondaryActions={secondaryActions}
        shell={shell}
        tab={tab}
        taskId={task.id}
      />
      {stripeActive && (
        <DevtoolMessage
          actions={[
            {
              actionText: `Set ${port} as primary port`,
              onAction: setPrimaryPort,
              loading: generatingPort,
            },
            {
              actionText: `Learn more...`,
              onAction: () => {
                window.open(
                  "https://codesandbox.io/docs/learn/devboxes/task#configuring-task-ports",
                  "_blank",
                );
              },
            },
          ]}
          onDismiss={() => setDismissStripePort(true)}
          variant="info"
        >
          <Text>
            Port <strong>{port}</strong> is not configured as primary port for
            task <strong>{task.name}</strong>
          </Text>
        </DevtoolMessage>
      )}
    </PreviewProvider>
  );
};
